/**
 * Selection Slice
 *
 * Manages the current model, bot, and language selection.
 * Also tracks the chat source and multimodal state, and handles
 * newChat and newChatWithBot actions.
 */

import type { StateCreator } from 'zustand';
import i18n from '../../../../i18n';
import { userService } from '../../../../shared/services/UserService';
import { useCitationStore } from '../../../../shared/store/citationStore';
import { useRightSidebarStore } from '../../../../shared/store/rightSidebarStore';
import { applyUserSettingsToBot } from '../../../../shared/utils/userSettings';
import type { Bot, Model } from '../../../../shared/types/api.types';
import type { ChatState, SelectionSlice } from '../types';

/**
 * Initial state for the Selection slice
 */
export const selectionInitialState: Pick<
  SelectionSlice,
  | 'currentModel'
  | 'currentBot'
  | 'currentLanguage'
  | 'chatSource'
  | 'hasMultimodalFilesInConversation'
> = {
  currentModel: null,
  currentBot: null,
  currentLanguage: i18n.language || 'en',
  chatSource: null,
  hasMultimodalFilesInConversation: false,
};

/**
 * Creates the selection slice with model, bot, and language selection actions.
 */
export const createSelectionSlice: StateCreator<
  ChatState,
  [
    ['zustand/devtools', never],
    ['zustand/persist', unknown],
    ['zustand/immer', never],
  ],
  [],
  SelectionSlice
> = (set, get) => ({
  // Initial state
  ...selectionInitialState,

  // Actions
  setCurrentModel: (model: Model) => {
    set({ currentModel: model }, false, 'selection/setModel');
  },

  setCurrentBot: (bot: Bot | null) => {
    set({ currentBot: bot }, false, 'selection/setBot');
  },

  setCurrentLanguage: (language: string) => {
    i18n.changeLanguage(language);
    set({ currentLanguage: language }, false, 'selection/setLanguage');
  },

  newChat: () => {
    // When starting a new chat from sidebar, set chatSource to 'sidebar'
    // This ensures rag_enabled will be false even if default bot exists

    // Hide citations when starting a new chat
    const { hideCitations } = useCitationStore.getState();
    hideCitations();

    // Hide data sources sidebar when starting a new chat
    const { hideDataSources } = useRightSidebarStore.getState();
    hideDataSources();

    set(
      state => ({
        ...state,
        messages: [],
        conversationId: null,
        chatSource: 'sidebar', // Mark as sidebar chat - rag_enabled will be false
        newChatTrigger: true,
        streamingMessage: null,
        error: null,
        isLoading: false,
        isStreaming: false,
        isWebSearchEnabled: false,
        isDeepResearchEnabled: false,
        focusInputRequested: true,
        hasMultimodalFilesInConversation: false,
      }),
      false,
      'selection/newChat'
    );
  },

  newChatWithBot: (bot: Bot) => {
    // Stop any active streaming
    get().stopStreaming();

    // Hide citations when starting a new chat with a bot
    const { hideCitations } = useCitationStore.getState();
    hideCitations();

    // Hide data sources sidebar when starting a new chat with a bot
    const { hideDataSources } = useRightSidebarStore.getState();
    hideDataSources();

    // Apply user settings to default assistant as a defensive measure
    const botWithUserSettings = bot;
    if (bot.is_default) {
      // Note: userService.getUser() is async, so this is non-blocking
      // User settings should already be applied by callers
      userService
        .getUser()
        .then(user => {
          if (user) {
            const updatedBot = applyUserSettingsToBot(bot, user);
            if (
              updatedBot.system_instructions !== bot.system_instructions ||
              updatedBot.temperature !== bot.temperature
            ) {
              set(
                state => ({ ...state, currentBot: updatedBot }),
                false,
                'selection/newChatWithBot:applyUserSettings'
              );
            }
          }
        })
        .catch(() => {
          // Non-critical: session creation failure doesn't prevent bot selection
        });
    }

    // Preset model if bot has a default_model_detail
    const currentModelBefore = get().currentModel;
    const modelToUse = bot.default_model_detail ?? currentModelBefore ?? null;

    set(
      state => ({
        ...state,
        messages: [],
        conversationId: null,
        currentBot: botWithUserSettings,
        currentModel: modelToUse,
        chatSource: 'assistant', // Mark as assistant chat - rag_enabled will be true
        newChatTrigger: true,
        streamingMessage: null,
        error: null,
        isLoading: false,
        isStreaming: false,
        isWebSearchEnabled: false,
        isDeepResearchEnabled: false,
        focusInputRequested: true,
        hasMultimodalFilesInConversation: false,
      }),
      false,
      'selection/newChatWithBot'
    );
  },

  clearChatOnly: () => {
    // Clear only chat-specific state, preserve conversations and other settings
    const currentState = get();

    // Hide data sources sidebar when clearing chat
    const { hideDataSources } = useRightSidebarStore.getState();
    hideDataSources();

    set(
      {
        messages: [],
        conversationId: null,
        streamingMessage: null,
        isLoading: false,
        isStreaming: false,
        error: null,
        focusInputRequested: true,
        // Keep everything else from current state
        allConversations: currentState.allConversations,
        conversations: currentState.conversations,
        currentModel: currentState.currentModel,
        currentBot: currentState.currentBot,
        currentLanguage: currentState.currentLanguage,
        showAllConversations: currentState.showAllConversations,
        newChatTrigger: false,
        isLoadingConversation: false,
        pendingRequests: 0,
        conversationsLoading: currentState.conversationsLoading,
        autoScrollEnabled: currentState.autoScrollEnabled,
        draftMessage: '',
        isWebSearchEnabled: currentState.isWebSearchEnabled,
        isDeepResearchEnabled: false,
        isDeletingConversation: currentState.isDeletingConversation,
        hasMultimodalFilesInConversation: false,
      },
      false,
      'selection/clearChatOnly'
    );
  },
});
